import { Reveal } from "./Reveal";
import { Check } from "lucide-react";

const TIERS = [
  {
    name: "Essential Pulse",
    label: "Automated Engine",
    tagline: "For small local operators who just need software infrastructure.",
    price: "$1,250",
    period: "/mo",
    alt: "or $2,997 for 90 days",
    setup: "+ $997 one-time setup fee",
    features: [
      "Automated Review Engine",
      "Google Business Profile Sync",
      "Smart Feedback Routing",
      "Automated Review & Feedback Requests",
      "Monthly Performance Report",
    ],
    cta: "Get Started",
    featured: false,
  },
  {
    name: "Concierge Plus",
    label: "Managed Hospitality",
    tagline: "For established businesses needing monthly white-glove oversight.",
    price: "$2,500",
    period: "/mo",
    alt: "or $6,800 for 90 days",
    setup: "+ $1,500 one-time setup fee",
    features: [
      "Everything in Essential Pulse",
      "Custom Response Writing",
      "Private Review Interception & Recovery (1–3 Star Protection Funnel)",
      "Database Reactivation Campaigns",
      "Monthly Reputation Audits",
    ],
    cta: "Start Partnership",
    featured: true,
  },
  {
    name: "Grand Concierge Suite",
    label: "Total Account Management",
    tagline: "For high-volume or multi-location brands needing end-to-end oversight.",
    price: "$3,200",
    period: "/mo",
    alt: "or $8,900 for 90 days",
    setup: "+ $2,000 one-time setup fee · waived with a 6-month agreement",
    features: [
      "Everything in Concierge Plus",
      "Total Account Control & Management",
      "Multi-Location Coordination",
      "Ad-Lander & Conversion Funnel Optimization",
      "Priority Strategy Sessions & Direct Line Support",
    ],
    cta: "Book Operations Audit",
    featured: false,
  },
];

const selectTier = (name) => {
  window.dispatchEvent(new CustomEvent("select-tier", { detail: name }));
};

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-background py-24 lg:py-32">
      <div className="container-luxe">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Reveal>
            <p className="overline">Investment Tiers</p>
            <h2 className="mt-4 serif text-[clamp(1.9rem,4vw,3.4rem)] leading-tight tracking-tight text-foreground">
              Pricing as transparent <span className="italic text-gold">as our service.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Higher search rankings, increased revenue, and valuable feedback — the
              goal stays the same at every tier.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-5 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pricing-tier-${i}`}
                className={`relative flex h-full flex-col p-8 lg:p-10 border transition-transform duration-500 hover:-translate-y-1 ${
                  t.featured
                    ? "bg-[var(--charcoal)] border-[var(--gold)] text-[var(--cream)] lg:-mt-4 lg:mb-[-16px] shadow-[0_30px_80px_-30px_rgba(184,147,90,0.55)]"
                    : "bg-background border-border"
                }`}
              >
                {t.featured && (
                  <div
                    data-testid="pricing-signature-badge"
                    className="absolute -top-px left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[var(--gold)] px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] text-[var(--charcoal)]"
                  >
                    Signature Choice
                  </div>
                )}

                <p className={`text-[10px] uppercase tracking-[0.24em] mb-3 ${t.featured ? "text-[var(--gold-soft)]" : "text-[var(--gold)]"}`}>
                  {t.label}
                </p>
                <h3 className={`serif text-2xl ${t.featured ? "text-[var(--cream)]" : "text-foreground"}`}>
                  {t.name}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed min-h-[42px] ${t.featured ? "text-[var(--cream)]/70" : "text-muted-foreground"}`}>
                  {t.tagline}
                </p>

                <div className="mt-7 flex items-end gap-1">
                  <span className={`serif text-5xl ${t.featured ? "gold-shimmer" : "text-foreground"}`}>
                    {t.price}
                  </span>
                  <span className={`mb-2 text-sm ${t.featured ? "text-[var(--cream)]/60" : "text-muted-foreground"}`}>
                    {t.period}
                  </span>
                </div>
                <p className={`mt-1 text-sm ${t.featured ? "text-[var(--cream)]/60" : "text-muted-foreground"}`} data-testid={`pricing-alt-${i}`}>
                  {t.alt}
                </p>
                <p className={`mt-3 text-xs uppercase tracking-[0.14em] leading-relaxed ${t.featured ? "text-[var(--gold-soft)]" : "text-[var(--gold)]"}`}>
                  {t.setup}
                </p>

                <ul className={`mt-8 space-y-3.5 border-t pt-8 ${t.featured ? "border-white/15" : "border-border"}`}>
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check
                        size={16}
                        className={`mt-0.5 shrink-0 ${t.featured ? "text-[var(--gold-soft)]" : "text-[var(--gold)]"}`}
                        strokeWidth={2}
                      />
                      <span className={t.featured ? "text-[var(--cream)]/85" : "text-foreground/85"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  onClick={() => selectTier(t.name)}
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-9 inline-flex items-center justify-center px-6 py-3.5 text-sm transition-colors duration-400 ${
                    t.featured
                      ? "bg-[var(--gold)] text-[var(--charcoal)] hover:bg-[var(--gold-light)]"
                      : "bg-secondary text-secondary-foreground hover:bg-[var(--gold)]"
                  }`}
                >
                  {t.cta}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
